import React from "react";

import { Box } from "@mui/material";

import Padding from "@/components/layout/Padding";
import { graphql } from "@/gql";
import { FragmentType } from "@/utils/type";

import AttachmentFile from "./AttachmentFile";
import AttachmentImage from "./AttachmentImage";
import AttachmentVideo from "./AttachmentVideo";
import MessageBubble, { MessageBubbleProps } from "./MessageBubble";
import MessageMeta, { MessageMetaProps } from "./MessageMeta";

interface Props extends MessageBubbleProps, MessageMetaProps {
  attachment: FragmentType<typeof messageAttachmentFragment>;
}

const messageAttachmentFragment = graphql(`
  fragment ChatMessageAttachment on Attachment {
    id
    byteSize
    contentType
    filename
    url
    downloadUrl
    thumbnail: url(variant: "medium")
    cloudinaryPreview
  }
`);

const MessageAttachment: React.FC<Props> = ({
  align,
  createdAt,
  isSent,
  isRead,
  attachment,
  isDeleted,
}) => {
  const renderAttachment = () => {
    const resourceType = attachment.contentType.split("/")[0];
    // pdf has resourceType "image", but we want it to be displayed as a
    // generic file instead.
    switch (resourceType) {
      case "video":
        return <AttachmentVideo attachment={attachment} isUploaded={isSent} />;
      case "image":
        return <AttachmentImage attachment={attachment} isUploaded={isSent} />;
      default:
        return <AttachmentFile attachment={attachment} isUploaded={isSent} />;
    }
  };

  return (
    <MessageBubble align={align} isDeleted={isDeleted}>
      <Box display="flex" flexDirection="column">
        <Padding horizontal="7px" top="8px">
          {renderAttachment()}
        </Padding>
        <Box marginBottom={2} />
        <Padding horizontal={3} bottom={2}>
          <MessageMeta
            createdAt={createdAt}
            isSent={isSent}
            isRead={isRead}
            fileSize={attachment.byteSize}
          />
        </Padding>
      </Box>
    </MessageBubble>
  );
};

export default MessageAttachment;
